/**
 * pricingFigures.js — the ONE place marketing copy derives cost sentences from.
 *
 * Source of the numbers: ./pricingFigures.json (change a figure there, never here).
 * Ruling (Nitish, Sep 6 2026; org canon pricing-claims): cost FIGURES live in four homes
 * only (Billing Guide "What Work Costs", /pricing FAQ, one shared cost-FAQ string, llms.txt);
 * every other surface states the mechanic and links to /pricing. The two markdown homes are
 * hand-written and GATED by marketing/scripts/check_pricing_surfaces.py --verify against the
 * same JSON; the FE homes import these constants so they cannot drift.
 *
 * Never name a default tier in copy ("built for speed" for Flash, "our deepest reasoning"
 * for Max). No em dashes. "shift" is internal vocabulary; copy says "working session".
 */
import figures from './pricingFigures.json';

const subRate = figures.credits_per_dollar.subscription;
const usd = (credits) => Math.round(credits / subRate);

export const PRICING_FIGURES = figures;
export const PRICING_FIGURES_DATED = figures.dated;

export const FLASH_SESSION_USD = {
  lo: usd(figures.session_credits.flash.lo),
  hi: usd(figures.session_credits.flash.hi),
};
export const MAX_SESSION_USD = {
  lo: usd(figures.session_credits.max.lo),
  hi: usd(figures.session_credits.max.hi),
};
export const FLASH_CEILING_USD = figures.ceilings_usd.flash;
export const MAX_CEILING_USD = figures.ceilings_usd.max;
export const STARTER_CREDITS = figures.starter_credits;
export const ENTRY_PLAN_USD_MONTH = figures.entry_plan_usd_month;

/** Mechanic line for every surface that is NOT one of the four figure homes. */
export const COST_MECHANIC_LINE =
  'You pay for the work, not the hire; the cost depends purely on how much work you assign.';

/** The shared cost FAQ answer (home #3). Each page keeps its own question wording
 *  ("How much is an AI employee?" etc.); only the answer is shared. */
export const COST_FAQ_ANSWER =
  'You pay for the work, not the hire. Hiring is free. Each time your employee works, they spend ' +
  'credits from your account: on Flash, built for speed, a working session typically runs under ' +
  `$${FLASH_CEILING_USD}. On Max, our deepest reasoning, the same session runs up to about ` +
  `$${MAX_CEILING_USD}. From there the cost depends purely on how much work you assign. ` +
  `Start free, no credit card needed, with ${STARTER_CREDITS} credits.`;

/** Compare-table pricing cell for the CellCog column. */
export const COMPARE_PRICING_ROW =
  `Plans from $${ENTRY_PLAN_USD_MONTH}/mo; usage-based, sessions run about ` +
  `$${FLASH_SESSION_USD.lo} to $${MAX_SESSION_USD.hi} by tier`;

/** Meta / structured-data description fragment (figure-free by ruling). */
export const COST_META_FRAGMENT = 'Usage-based: pay for the work, not the hire.';

/** /pricing is a figure home, so its metas may carry the two ceilings (Nitish, Sep 7 2026:
 *  a pricing-intent snippet earns the number; no tier noun, Flash and Max are tiers, not modes).
 *  Every other page's meta stays figure-free. Lengths: meta <=165 chars for the snippet. */
export const PRICING_META_DESCRIPTION =
  `Plans from $${ENTRY_PLAN_USD_MONTH}/month. Pay for the work, not the hire: a working session ` +
  `runs under $${FLASH_CEILING_USD} on Flash and up to about $${MAX_CEILING_USD} on Max. ` +
  'Start free, no credit card needed.';
export const PRICING_SOCIAL_DESCRIPTION =
  `Hiring is free. A working session runs under $${FLASH_CEILING_USD} on Flash and up to about ` +
  `$${MAX_CEILING_USD} on Max, and the cost depends purely on how much work you assign. ` +
  'Start free, no credit card needed.';
export const PRICING_SHORT_DESCRIPTION =
  `Hiring is free. A working session runs under $${FLASH_CEILING_USD} on Flash and up to about ` +
  `$${MAX_CEILING_USD} on Max. Start free, no credit card needed.`;

/** The exact llms.txt pricing line the verify gate expects (mirrors the Python rendering). */
export const LLMS_TXT_PRICING_LINE = figures.llms_txt_pricing_line
  .replace('{flash_ceiling_usd}', String(FLASH_CEILING_USD))
  .replace('{max_ceiling_usd}', String(MAX_CEILING_USD));
